import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { formatDisplayTime } from "../lib/display-time";
import {
  COMPANY_CHANNEL_TARGET,
  EMPTY_CHANNEL_DRAFT,
  channelSubtitle,
  createChannel,
  decodeChannelTargetValue,
  encodeChannelTargetValue,
  groupChannels,
  validateChannelDraft,
  type Channel,
  type ChannelDraft,
  type ChannelTargetOption,
} from "../data/channels";

// The console's board list, grouped exactly as plate 07 groups it on the phone: Company, then
// Sites, then Shift groups. A supervisor and a guard should be able to say "the RRVAMC board" and
// mean the same row.
//
// Creating a channel is behind channel.manage; the server enforces it regardless.

export function ChannelList({
  channels,
  selectedChannelId,
  unreadChannelIds,
  onSelect,
  timeZone,
}: {
  readonly channels: readonly Channel[];
  readonly selectedChannelId: string | null;
  readonly unreadChannelIds: ReadonlySet<string>;
  readonly onSelect: (channel: Channel) => void;
  readonly timeZone: string;
}) {
  const groups = groupChannels(channels);

  if (groups.length === 0) {
    return (
      <p className="surface__state" role="status">
        No channels have been set up for this account yet.
      </p>
    );
  }

  return (
    <div className="channel-list">
      {groups.map((group) => (
        <section className="channel-list__group" key={group.kind} aria-label={group.label}>
          <h3 className="channel-list__group-label">{group.label}</h3>
          <ul className="channel-list__items">
            {group.channels.map((channel) => (
              <li key={channel.channelId}>
                <button
                  type="button"
                  className={
                    channel.channelId === selectedChannelId
                      ? "channel-row channel-row--selected"
                      : "channel-row"
                  }
                  aria-current={channel.channelId === selectedChannelId}
                  onClick={() => onSelect(channel)}
                >
                  <span className="channel-row__text">
                    <span className="channel-row__name">{channel.name}</span>
                    {/* Plate 07 line two is always the last poster and when, or "No posts yet". */}
                    <span className="channel-row__meta">
                      {channelSubtitle(channel, (iso) => displayOrNull(iso, timeZone))}
                    </span>
                  </span>
                  {unreadChannelIds.has(channel.channelId) ? (
                    <span
                      className="channel-row__dot"
                      aria-label="Unread board activity"
                      title="Unread board activity"
                    />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** `formatDisplayTime` returns its own placeholder; the shared subtitle wants null for "unknown". */
function displayOrNull(iso: string, timeZone: string): string | null {
  const formatted = formatDisplayTime(iso, timeZone);
  return formatted === "—" ? null : formatted;
}

export function ChannelCreateForm({
  client,
  accountId,
  targetOptions,
  targetNote,
  onCreated,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly targetOptions: readonly ChannelTargetOption[];
  /** Honest note when the site list could not be read, so "company-wide only" is explained. */
  readonly targetNote: string | null;
  readonly onCreated: () => void;
}) {
  const [draft, setDraft] = useState<ChannelDraft>(EMPTY_CHANNEL_DRAFT);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const selectedValue = encodeChannelTargetValue({
    kind: draft.kind,
    id: draft.targetId,
    label: "",
  });

  function update(patch: Partial<ChannelDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy) return;
    const validation = validateChannelDraft(draft);
    if (!validation.ok) {
      setFailure(validation.reason);
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const outcome = await createChannel(client, accountId, draft);
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason);
      return;
    }
    setDraft(EMPTY_CHANNEL_DRAFT);
    setReceipt("Channel created.");
    onCreated();
  }

  return (
    <form className="channel-form" aria-label="New channel" onSubmit={(event) => event.preventDefault()}>
      <label className="channel-form__field">
        <span className="channel-form__label">Name</span>
        <input
          type="text"
          value={draft.name}
          disabled={busy}
          maxLength={120}
          onChange={(event) => update({ name: event.target.value })}
        />
      </label>

      <label className="channel-form__field">
        <span className="channel-form__label">For</span>
        <select
          value={selectedValue}
          disabled={busy}
          onChange={(event) => {
            const option = decodeChannelTargetValue(event.target.value, targetOptions);
            update({ kind: option.kind, targetId: option.id });
          }}
        >
          {targetOptions.map((option) => (
            <option key={encodeChannelTargetValue(option)} value={encodeChannelTargetValue(option)}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {targetNote !== null ? <p className="channel-form__note">{targetNote}</p> : null}

      <p className="channel-form__note">
        Channels are announcement boards in this release: supervisors post, everyone in the channel
        reads. There is no replying yet, on the console or on the phone.
      </p>

      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Creating..." : "Create channel"}
        </button>
        <button
          type="button"
          className="builder__navbtn"
          disabled={busy}
          onClick={() => {
            setDraft(EMPTY_CHANNEL_DRAFT);
            setFailure(null);
            setReceipt(null);
          }}
        >
          Clear
        </button>
      </div>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

export { COMPANY_CHANNEL_TARGET };
